import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { CmsTemplateCopy } from '../../models/content';
import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';

/**
 * Template-copy fragment editor. The key is fixed (it joins to the site's
 * registry); the copy is plain text with {placeholder} substitutions. Saving
 * validates that every placeholder used is one the fragment's description
 * declares — an unknown one renders literally on hundreds of pages.
 */
@Component({
  selector: 'app-content-template-copy',
  templateUrl: './content-template-copy.component.html',
})
export class ContentTemplateCopyComponent implements OnInit {
  fragment!: CmsTemplateCopy;
  form!: FormGroup;
  /** Placeholders the description declares, e.g. ['region', 'suburb']. */
  allowedVars: string[] = [];
  /** Some fragments carry an image (the help-band photo). Any fragment MAY —
   *  the site simply ignores an image its template doesn't render. */
  imageFileId: number | null = null;
  imagePreview: string | null = null;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    this.fragment = this.config.data.fragment;
    this.imageFileId = typeof this.fragment.imageFileId === 'number' ? this.fragment.imageFileId : null;
    if (this.imageFileId != null) {
      this.fileService.getPreviewLink(String(this.imageFileId)).subscribe({
        next: (url) => (this.imagePreview = url),
        error: () => {},
      });
    }
    this.allowedVars = [...(this.fragment.description ?? '').matchAll(/\{([a-zA-Z]+)\}/g)].map((m) => m[1]);
    this.form = this.fb.group({
      copy: new FormControl(this.fragment.copy ?? '', [Validators.required, Validators.pattern(/\S/)]),
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const copy = (this.form.getRawValue().copy as string).trim();

    const used = [...copy.matchAll(/\{([a-zA-Z]+)\}/g)].map((m) => m[1]);
    const unknown = used.filter((v) => !this.allowedVars.includes(v));
    if (unknown.length) {
      this.modalService.generic({
        title: "This sentence can't be saved yet",
        copy: [
          `{${unknown[0]}} isn't one of the fill-ins this sentence supports${this.allowedVars.length ? ` (${this.allowedVars.map((v) => '{' + v + '}').join(', ')})` : ''} — it would show as literal curly braces on every page.`,
        ],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    const { _meta, ...fields } = this.fragment as any;
    this.ref.close({
      saved: true,
      fragment: { id: _meta?.id, ...fields, copy, imageFileId: this.imageFileId },
    });
  }

  uploadImage(): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload fragment image',
      bucketPrefix: 'template-copy',
      // Raster only — the site's asset globs skip GIF/SVG.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      this.imageFileId = file.id;
      if (file.link) this.imagePreview = file.link;
    });
  }

  removeImage(): void {
    this.imageFileId = null;
    this.imagePreview = null;
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}
